import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'

import PropTypes from 'prop-types'

import debounce from 'lodash/debounce'
import filter from 'lodash/filter'
import find from 'lodash/find'
import indexOf from 'lodash/indexOf'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'

import Checkbox from '@material-ui/core/Checkbox'
import Grid from '@material-ui/core/Grid'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import Paper from '@material-ui/core/Paper'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'

import I18n from '@galo-monitor/i18n'
import { nextPage, previousPage } from '@galo-monitor/icons'
import colors from '@galo-monitor/styles/colors'
import Button from '@galo-monitor/web-components/Button'
import Icon from '@galo-monitor/web-components/Icon'

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 'auto',
    backgroundColor: colors.lightGrey3,
    paddingTop: 20,
    paddingBottom: 20
  },
  paper: {
    width: 250,
    height: 300,
    overflow: 'auto'
  },
  button: {
    margin: theme.spacing(0.5, 0)
  }
}))

function not(a, b) {
  return a.filter((value) => b.indexOf(value) === -1)
}

const TransferListStyled = (props) => {
  const {
    value,
    label,
    labelLeft,
    labelRight,
    onChange,
    options,
    loading: externalLoading,
    movedToLeft,
    movedToRight
  } = props

  const mounted = useRef(false)

  const classes = useStyles()
  const [checked, setChecked] = useState([])
  const [left, setLeft] = useState([])
  const [right, setRight] = useState([])
  const [loading, setLoading] = useState(externalLoading)

  const debouncedRemoveLoading = useCallback(
    debounce(() => {
      if (mounted.current) {
        setLoading(false)
      }
    }, 300),
    []
  )

  useEffect(() => {
    if (externalLoading) {
      setLoading(true)
    } else {
      debouncedRemoveLoading()
    }

  }, [debouncedRemoveLoading, externalLoading])

  useEffect(() => {
    setRight(filter(options, option => indexOf(value, option.value) > -1))
  }, [options, value])

  useEffect(() => {
    setLeft(filter(options, option => indexOf(value, option.value) < 0))
  }, [options, value])

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const leftChecked = useMemo(
    () => filter(left, (item) => find(checked, check => check === item.value)),
    [checked, left]
  )

  const rightChecked = useMemo(
    () => filter(right, (item) => find(checked, check => check === item.value)),
    [checked, right]
  )

  const handleToggle = useCallback(
    (val) => () => {
      const currentIndex = checked.indexOf(val.value)
      const newChecked = [...checked]

      if (currentIndex === -1) {
        newChecked.push(val.value)
      } else {
        newChecked.splice(currentIndex, 1)
      }

      setChecked(newChecked)
    },
    [checked]
  )

  const handleCheckedRight = useCallback(
    () => {
      const newRight = right.concat(leftChecked)
      setRight(newRight)
      setLeft(not(left, leftChecked))
      setChecked([])
      onChange(map(newRight, item => item.value))
      movedToRight(leftChecked)

    },
    [left, leftChecked, movedToRight, onChange, right]
  )

  const handleCheckedLeft = useCallback(
    () => {
      const newRight = not(right, rightChecked)
      setLeft(left.concat(rightChecked))
      setRight(not(right, rightChecked))
      setChecked([])
      onChange(map(newRight, item => item.value))
      movedToLeft(rightChecked)
    },
    [left, movedToLeft, onChange, right, rightChecked]
  )

  const customList = useCallback(
    (items) => (
      <Paper className={ classes.paper }>
        <List dense component="div" role="list">
          {loading ? (
            <ListItem role="listitem">
              <I18n>loading</I18n>
            </ListItem>
          ) : (
            <>
              {isEmpty(items) ? (
                <ListItem role="listitem">
                  <I18n>empty select options</I18n>
                </ListItem>
              ) : map(items, (val) => {
                const labelId = `transfer-list-item-${ val }-label`

                return (
                  <ListItem key={ val.value } role="listitem" button onClick={ handleToggle(val) }>
                    <ListItemIcon>
                      <Checkbox
                        color="primary"
                        checked={ checked.indexOf(val.value) !== -1 }
                        tabIndex={ -1 }
                        disableRipple
                        size="small"
                        inputProps={ { 'aria-labelledby': labelId } }
                      />
                    </ListItemIcon>
                    <ListItemText id={ labelId } primary={ val.label } />
                  </ListItem>
                )
              })}
            </>
          )}
          <ListItem />
        </List>
      </Paper>
    ),
    [checked, classes.paper, handleToggle, loading]
  )

  return (
    <Grid container justify="space-evenly" alignItems="center" className={ classes.root }>
      {label && (
        <Grid item xs={ 12 }>
          <Typography style={ { marginBottom: 10 } }>
            {label}
          </Typography>
        </Grid>
      )}
      <Grid item>
        {labelLeft && (
          <Typography style={ { marginBottom: 10 } }>
            {labelLeft}
          </Typography>
        )}
        {customList(left)}
      </Grid>
      <Grid item style={ { margin: '0 15px' } }>
        <Grid container direction="column" alignItems="center">
          <Button
            id="transferLeftToRight"
            size="small"
            className={ classes.button }
            onClick={ handleCheckedRight }
            disabled={ leftChecked.length === 0 }
            aria-label="move selected right"
          >
            <Icon size={ 12 } icon={ nextPage } color={ leftChecked.length === 0 ? undefined : colors.white } />
          </Button>
          <Button
            id="transferRightToLeft"
            size="small"
            className={ classes.button }
            onClick={ handleCheckedLeft }
            disabled={ rightChecked.length === 0 }
            aria-label="move selected left"
          >
            <Icon size={ 12 } icon={ previousPage } color={ rightChecked.length === 0 ? undefined : colors.white } />
          </Button>
        </Grid>
      </Grid>
      <Grid item>
        {labelRight && (
          <Typography style={ { marginBottom: 10 } }>
            {labelRight}
          </Typography>
        )}
        {customList(right)}
      </Grid>
    </Grid>
  )
}


TransferListStyled.propTypes = {
  value: PropTypes.array,
  label: PropTypes.string,
  labelLeft: PropTypes.string,
  labelRight: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.array,
  movedToLeft: PropTypes.func,
  movedToRight: PropTypes.func,
  loading: PropTypes.bool
}

TransferListStyled.defaultProps = {
  value: [],
  label: '',
  labelLeft: '',
  labelRight: '',
  onChange: () => {},
  movedToLeft: () => {},
  movedToRight: () => {},
  options: [],
  loading: false
}


export default TransferListStyled
